import type { CountryCode } from '@hotjar/country-lib';
import type { PartialDeep } from 'type-fest';
import { useTranslation } from '@hotjar/translations';
import { tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { Heading } from '../EditOrderContainer.styles';
import { InvoiceDetailsForm } from '~/subscription-management/components/invoice-details/form/InvoiceDetailsForm';
import { FORM_NAME, trackFormSubmit } from '~/subscription-management/utils/tracking';
import { shouldMigrateAddress } from '~/subscription-management/utils/migrated-customers.utils';
import type { Customer } from '../../../../api/common/model/customer';
import { UnstructuredAddressInfo } from '~/subscription-management/components';

export interface FormInputs {
  firstName: string;
  lastName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  country: CountryCode;
  state: string | null;
  county: string | null;
  zipCode: string | null;
  companyName: string;
  taxNumber: string | null;
  emailAddress: string;
}

interface Props {
  formId: string;
  accountId: number;
  defaultValues: PartialDeep<FormInputs, { recurseIntoArrays: true }>;
  onSubmit(data: FormInputs): void;
  hasError?: boolean;
  billingAddress: Customer['billingAddress'];
}

const S = {
  UnstructuredAddressInfo: styled(UnstructuredAddressInfo)`
    margin-bottom: ${tokens.spacer.x3};
  `,
};

export function InvoiceDetails({
  defaultValues,
  formId,
  onSubmit,
  accountId,
  billingAddress,
  hasError = false,
}: Props): JSX.Element {
  const { t: tHeader } = useTranslation('checkout', { keyPrefix: 'header' });
  const { t: tInvoiceDetails } = useTranslation('checkout', {
    keyPrefix: 'wizard.invoice-details',
  });

  return (
    <>
      <Heading textType="header4" as="h1">
        {tHeader('invoice-details')}
      </Heading>
      {shouldMigrateAddress(billingAddress) && (
        <S.UnstructuredAddressInfo
          unstructuredAddress={billingAddress?.unstructuredAddress}
          taxNumber={defaultValues.taxNumber}
          companyName={defaultValues.companyName}
          extendedVersion
        />
      )}
      <InvoiceDetailsForm
        onSubmit={(data: FormInputs) => {
          trackFormSubmit({ name: FORM_NAME.invoiceDetails, accountId, submitSuccess: true });
          onSubmit(data);
        }}
        defaultValues={defaultValues}
        formId={formId}
        accountId={accountId}
        formName={FORM_NAME.invoiceDetails}
        error={hasError ? tInvoiceDetails('error.generic') : undefined}
      />
    </>
  );
}
